import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import loadable from 'components/hoc/loadable';
import CommonNotes from 'app/Campaigns/Dialer/ManageNotes';
import SimpleModal from 'components/base/SimpleModal';
import SVG from 'components/base/SVG';
import Select from 'components/Select';
import { validate } from 'utils/validation';
import InfoAlert from 'app/components/InfoAlert';
import SkipTraceModal from 'app/components/SkipTraceModal';
import {
  getAppendJobEstimate,
  getContactIds,
  loadContact,
  loadContactDocuments,
  loadContactNotes,
  resetContactExtras,
  sameContactId,
  saveContact,
  saveContactNote,
  selectAppendGroups,
  selectContact,
  selectContactDocumentDeleting,
  selectContactDocumentUploading,
  selectContactDocuments,
  selectContactExtrasContactId,
  selectContactNote,
  selectContactNoteLoaded,
  selectContactNoteSaving,
  selectContactStatuses,
  selectContactTypes,
  selectLoading,
  deleteContactDocument,
  uploadContactDocument,
} from 'data/contacts';
import {
  selectPabEnabled,
  selectProfile,
  selectIsClickToDialLoaded,
  openClickToDial,
} from 'data/user';
import { defaultGroupId } from 'reducers';
import dropdownNormalize from 'utils/normalizers/dropdown';
import noop from 'utils/noop';
import { isPropStream } from 'utils/brand';
import { isRedactedNumber } from 'utils/contactUtil';
import ClickToDial from 'components/ClickToDial';

import ContactInformationModal from './ContactInformationModal';
import ContactInfoCard from './ContactInfoCard';
import GeneralInformationModal from './GeneralInformationModal';
import Hero from './Hero';
import {
  blankContact,
  displayName,
  hasContactInformation,
  hasIdentity,
  isPersistable,
  toDraft,
} from './contactState';

import css from './style.scss';

/**
 * Contact Details profile overlay (ATAI-112 / S6).
 *
 * Figma: 383-21724 / 383-25494 (shell), 383-27031 (General Information),
 * 383-26723 (Contact Information), 1105-67761 (add new).
 *
 * Follows the Property Detail pattern: same route
 * (`/contact/:groupId/:contactId`, `new` = add), opened over the contact list,
 * which stays mounted behind it. The shell READS the contact; editing happens
 * in two nested modals, with Contact Type/Status live here.
 *
 * Contact data reuses the existing PS4 `loadContact`/`saveContact` actions
 * unchanged. Notes and documents come from propstream-core and are held in the
 * store (see data/contacts/psCoreActions).
 */

const SECTION = { GENERAL: 'general', CONTACT_INFO: 'contactInfo' };

/* SimpleModal has no loading state of its own, so the body goes through the
   shared loadable HOC (no `isHover` -> content hidden, shared Loader shown). */
const LoadableBody = loadable(({ children }) => children);

const formatAddress = (street, city, state, zip) => {
  const tail = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, tail].filter(Boolean).join(', ');
};

const MailingAddress = ({ propertyAddress, mailingAddress }) => (
  <div className={css.addressBlock}>
    <div className={css.addressTitle}>Mailing Address</div>
    {!propertyAddress ? null : <div className={css.addressLine}>{propertyAddress}</div>}
    {!mailingAddress ? null : <div className={css.addressLine}>{mailingAddress}</div>}
    {propertyAddress || mailingAddress ? null : (
      <div className={css.addressEmpty}>No address on file.</div>
    )}
  </div>
);

const ContactProfile = ({ location, history }) => {
  const segments = location.pathname.split('/');
  const groupId = segments[2];
  const contactId = segments[3];
  const isNewContact = contactId === 'new';
  const parentPath = `/contact/${groupId}`;

  const dispatch = useDispatch();
  const types = useSelector(selectContactTypes);
  const statuses = useSelector(selectContactStatuses);
  const loading = useSelector(selectLoading);
  const pabEnabled = useSelector(selectPabEnabled);
  const profile = useSelector(selectProfile);
  const isClickToDialLoaded = useSelector(selectIsClickToDialLoaded);
  const storedContact = useSelector(selectContact);
  const appendGroups = useSelector(selectAppendGroups);

  const extrasContactId = useSelector(selectContactExtrasContactId);
  const savedNote = useSelector(selectContactNote);
  const notesLoaded = useSelector(selectContactNoteLoaded);
  const noteSaving = useSelector(selectContactNoteSaving);
  const documents = useSelector(selectContactDocuments);
  const uploading = useSelector(selectContactDocumentUploading);

  const deletingDocument = useSelector(selectContactDocumentDeleting);
  const [contact, setContact] = useState(blankContact());
  const [openSection, setOpenSection] = useState(null);
  const [skipTracing, setSkipTracing] = useState(false);
  const [note, setNote] = useState(savedNote || '');
  // `PUT /contacts/new` answers with the contact groups, not the saved contact,
  // so a new contact's id never comes back. Guard against a second id-less save,
  // which the backend would treat as another create rather than an update.
  const creating = useRef(false);

  useEffect(() => {
    if (isNewContact) {
      setContact(blankContact());
      // a new contact starts on the name/company step, as the design shows
      setOpenSection(SECTION.GENERAL);
      return;
    }
    dispatch(
      loadContact(Number(contactId), ({ response }) => {
        setContact(toDraft(response?.response || {}));
      })
    );
  }, [contactId]);

  useEffect(() => {
    if (isNewContact || !storedContact || !storedContact.get('id')) return;
    setContact(toDraft(storedContact));
  }, [storedContact]);

  /* Notes and documents are keyed on the contact id, so they can only be
     fetched once the contact exists -- including one just created here. */
  useEffect(() => {
    if (!contact.id) {
      dispatch(resetContactExtras());
      return;
    }
    dispatch(loadContactNotes(contact.id));
    dispatch(loadContactDocuments(contact.id));
  }, [contact.id]);

  // the editor edits a local copy; the store holds what was last persisted.
  // ATAI-388: re-seeded on a contact change too, and during render, so no frame
  // paints a stale draft.
  const [noteSeed, setNoteSeed] = useState({ contactId: extrasContactId, note: savedNote });
  if (noteSeed.contactId !== extrasContactId || noteSeed.note !== savedNote) {
    setNoteSeed({ contactId: extrasContactId, note: savedNote });
    setNote(savedNote || '');
  }

  const handleClose = () => history.push(parentPath);

  /**
   * Resolve the id of the contact we just created, by diffing the all-contacts
   * id list around the save (a new contact is not attached to any group, so
   * group '0' is the one place it is guaranteed to appear). Exactly one added
   * id means we found it; anything else is ambiguous and we do not guess.
   */
  const createThenAdopt = (next) => {
    dispatch(
      getContactIds(defaultGroupId, ({ response: before }) => {
        const known = new Set(before || []);
        dispatch(
          saveContact(next, () => {
            dispatch(
              getContactIds(defaultGroupId, ({ response: after }) => {
                const added = (after || []).filter((id) => !known.has(id));
                // adopt it: every later save now carries an id, so it updates
                if (added.length === 1) setContact((prev) => ({ ...prev, id: added[0] }));
                // could not identify it - hand back to the list rather than
                // risk a second id-less save creating a duplicate
                else history.push(parentPath);
              })
            );
          })
        );
      })
    );
  };

  /**
   * `UserService.saveContact` branches on `contact.isPersisted()` -- an `id` in
   * the payload updates that row, no `id` runs a dedupe query and otherwise
   * inserts. So an already-loaded contact must always round-trip its `id`.
   */
  const persist = (next) => {
    setContact(next);

    // the save endpoint rejects a contact without both of these
    if (!isPersistable(next)) return;

    if (next.id) {
      dispatch(saveContact(next));
      return;
    }

    // Creating. Wait until there is a name/company too, so we never insert an
    // otherwise-empty row, and create exactly once.
    if (!hasIdentity(next) || creating.current) return;
    creating.current = true;
    createThenAdopt(next);
  };

  const handleTypeStatusChange = (name, value) => persist({ ...contact, [name]: value });

  const handleSectionSave = (patch) => {
    setOpenSection(null);
    persist({ ...contact, ...patch });
  };

  // Same guards as the Dialer's note save (Campaigns/Dialer handleSave): the
  // validate() boundary, and a 4000-character cap measured on the plain text so
  // that markup the editor emits is not counted against the user. ps-core stores
  // the note as a @Lob and validates nothing, so this cap is the only one there is.
  const handleNoteSave = () => {
    if (!contact.id || noteSaving) return;

    validate(() => {
      const plainTextLength = note.replace(/<[^>]*>/g, '').trim().length;

      if (plainTextLength > 4000) {
        dispatch(
          InfoAlert.open({
            title: 'Invalid Note Length',
            infoText: 'Note length cannot exceed 4000 characters.',
          })
        );
        return;
      }

      dispatch(saveContactNote(contact.id, note));
    });
  };

  // ButtonUpload inside CommonNotes hands back { value: { file } }
  const handleUploadFile = ({ value: { file } = {} } = {}) => {
    if (file && contact.id && !uploading) dispatch(uploadContactDocument(contact.id, file));
  };

  // CommonNotes' remove button hands back the attachment's INDEX, not the file
  // (renderAttachments: `onRemoveAttachment(attachments.length - 1 - i)`), so
  // map it back to the row before hitting the endpoint.
  const handleRemoveFile = (index) => {
    const doc = (documents || [])[index];

    if (doc?.id && contact.id && !deletingDocument) {
      dispatch(deleteContactDocument(contact.id, doc.id));
    }
  };

  /**
   * Skip Trace -- the SYNCHRONOUS flow, as on Property Detail.
   *
   * `isPropertySkipTrace` selects it inside SkipTraceModal: it posts to
   * `/user/contacts/property-skip-trace` for the single contact and reports
   * "completed", instead of the bulk append-job path ContactsList uses. Because
   * it completes inline we reload the contact straight into the open profile.
   */
  const handleSkipTrace = () => {
    if (skipTracing || !contact.id) return;
    setSkipTracing(true);

    const contactIds = [contact.id];
    const reprocessEnabled = Boolean(contact.appendedPhones && contact.appendedPhones.length);

    dispatch(
      getAppendJobEstimate({ contactIds, appendMode: 'BOTH', reprocessEnabled }, ({ response }) => {
        if (response?.message?.includes('There is an existing append job')) {
          dispatch(
            InfoAlert.open({
              title: 'Skip Trace in Progress',
              infoText:
                'There is a Skip Trace currently in progress. Please try again once it is complete.',
            })
          );
          setSkipTracing(false);
          return;
        }

        dispatch(
          SkipTraceModal.open({
            groups: appendGroups,
            reprocessEnabled,
            contactIds,
            selectedProperties: 1,
            isPropertySkipTrace: true,
            onSuccess: () => {
              dispatch(
                loadContact(Number(contact.id), ({ response: loaded }) => {
                  setContact(toDraft(loaded?.response || {}));
                  setSkipTracing(false);
                })
              );
            },
            // No match / already traced / error: reload too (a duplicate returns cached data), and
            // clear skipTracing so the button works again
            onFailure: () => {
              dispatch(
                loadContact(
                  Number(contact.id),
                  ({ response: loaded }) => {
                    setContact(toDraft(loaded?.response || {}));
                    setSkipTracing(false);
                  },
                  () => setSkipTracing(false)
                )
              );
            },
            onCancel: () => setSkipTracing(false),
          })
        );
      })
    );
  };

  // Contact details can only hang off a contact that exists: until the
  // name/type/status have been saved there is no row to edit, and an id-less
  // save would create a second contact instead of updating this one.
  const isSaved = Boolean(contact.id);

  // Click-to-dial on the contact's phone numbers, wired exactly like the
  // Contacts list (SearchTable/ContactCellRenderer) and Property Detail: the
  // number is a clickable span that dispatches `openClickToDial`, gated by the
  // softphone integration being loaded AND a PropStream brand. The actual call
  // flow lives in the <ClickToDial> modal mounted below.
  const userType = profile?.get?.('type');
  const shouldRenderClickToDial = ['FULL', 'TEAM_FULL', 'TEAM'].includes(userType);
  const canDial = isClickToDialLoaded && isPropStream();

  const dialPhone = (phone) => {
    // A saved contact with a redacted number can't be dialed (same guard as
    // Property Detail), and a never-saved contact has no id to key the call on.
    if (!canDial || !contact.id || !phone || isRedactedNumber(phone.phone)) return;
    dispatch(
      openClickToDial({
        contactId: contact.id,
        phonenumber: String(phone.phone || '').replace(/\D/g, ''),
        litigatorFlag: contact.litigatorFlag,
        myDnc: phone.myDNC,
        publicDnc: phone.publicDNC,
        score: phone.score,
        pab: phone.pab,
      })
    );
  };

  // One loading view for the whole notes section: the initial fetch, saving a
  // note, and uploading a file. CommonNotes' own Save takes no loading state and
  // it is shared with Property Detail and the Dialer, so the treatment lives
  // here. Consistent with the modal body, the content is hidden rather than
  // dimmed while the request is in flight.
  // ATAI-388: this view stays mounted across contacts, so wait for THIS one's.
  const notesBusy =
    !sameContactId(extrasContactId, contact.id) ||
    !notesLoaded ||
    noteSaving ||
    uploading ||
    deletingDocument;

  const propertyAddress = formatAddress(
    contact.streetAddress,
    contact.city,
    contact.state,
    contact.zip
  );
  const mailingAddress = contact.mailAddressSame
    ? null
    : formatAddress(
        contact.mailStreetAddress,
        contact.mailCity,
        contact.mailState,
        contact.mailZip
      );

  const taxonomy = [
    { name: 'typeId', label: 'Contact Type', options: dropdownNormalize(types) },
    { name: 'statusId', label: 'Status', options: dropdownNormalize(statuses) },
  ];

  return (
    <>
      <SimpleModal
        isOpen
        ignoreEscAndClickOutside
        uniqueId="contactDetails"
        wrapperClassName={css.modalWrapper}
        modalClassName={css.modal}
        modalHeaderWrapperClassName={css.headerWrap}
        modalBodyClassName={css.bodyWrap}
        modalFooterWrapperClassName={css.noFooter}
        customFooter={<></>}
        customHeader={
          <div className={css.header}>
            <div className={css.caption}>Contact Details</div>
            <button
              type="button"
              className={css.closeButton}
              onClick={handleClose}
              aria-label="Close"
            >
              <SVG icon="cp-close" className={css.closeIcon} />
            </button>
          </div>
        }
        modalBody={
          <LoadableBody isLoading={loading}>
            <div className={css.body}>
              <Hero
                showIdentity={hasIdentity(contact)}
                name={displayName(contact)}
                companyName={contact.companyName}
                onEdit={() => setOpenSection(SECTION.GENERAL)}
              />

              <div className={css.taxonomyRow}>
                {taxonomy.map(({ name, label, options }) => (
                  <div className={css.taxonomyField} key={name}>
                    <div className={css.fieldLabel}>
                      {label} <span className={css.required}>*</span>
                    </div>
                    <Select
                      name={name}
                      lists={options}
                      selectedValue={contact[name]}
                      valueKey="value"
                      labelKey="label"
                      autoFlip
                      onSelect={({ value }) => handleTypeStatusChange(name, value)}
                    />
                  </div>
                ))}
              </div>

              {notesBusy ? (
                <div className={css.notesLoading}>
                  <SVG icon="spinner" className={css.notesLoadingSpinner} />
                </div>
              ) : (
                /* The shared notes editor, as used by Property Detail (Tags) and
                   the Dialer -- its own toolbar, upload button, attachment chips
                   and Save, so none of that is duplicated here. */
                <CommonNotes
                  key={contact.id}
                  className={css.notes}
                  value={note}
                  placeholder="Add notes here"
                  onChange={setNote}
                  onSave={handleNoteSave}
                  notesDirty={note !== (savedNote || '')}
                  attachments={documents || []}
                  onUpload={handleUploadFile}
                  // propstream-core exposes list + upload for contact documents
                  // but neither download nor delete, so the chips stay inert.
                  attachmentsDisabled
                  onRemoveAttachment={handleRemoveFile}
                  onDownloadAttachment={noop}
                />
              )}

              <ContactInfoCard
                contact={contact}
                isNewContact={isNewContact}
                isSaved={isSaved}
                showContactInfo={hasContactInformation(contact)}
                skipTracing={skipTracing}
                propertyAddress={propertyAddress}
                mailingAddress={mailingAddress}
                onEdit={() => setOpenSection(SECTION.CONTACT_INFO)}
                onSkipTrace={handleSkipTrace}
                MailingAddress={MailingAddress}
                canDial={canDial}
                onDialPhone={dialPhone}
              />
            </div>
          </LoadableBody>
        }
      />

      {openSection !== SECTION.GENERAL ? null : (
        <GeneralInformationModal
          contact={contact}
          onCancel={() => setOpenSection(null)}
          onSave={handleSectionSave}
        />
      )}

      {openSection !== SECTION.CONTACT_INFO ? null : (
        <ContactInformationModal
          contact={contact}
          pabEnabled={pabEnabled}
          onCancel={() => setOpenSection(null)}
          onSave={handleSectionSave}
        />
      )}

      {isPropStream() && shouldRenderClickToDial && <ClickToDial profile={profile} />}
    </>
  );
};

export default withRouter(ContactProfile);
